// The translator's notes in the UI language. The engine gives every note as data
// (TranslationResult.noteItems: a stable `id`, the words it quotes as `args`, and the English
// sentence as `text`); a note whose id the app knows is rendered from i18n (t.translator.note)
// with its arguments. A result without noteItems (an older engine) is still read by pattern from
// the English sentences. A note the app knows neither way is shown in English and labelled as
// such, so a new engine note is never lost, only untranslated until its id is added.
//
// The notes the app knows are a registry: each feature declares its own in
// translator/notes/<feature>.notes.ts (id → argument names, Finnish, English; note-defs.ts), collected
// here with import.meta.glob. To add a note: add it to its feature's notes file (or a new one); nothing
// here or in i18n changes.

import type { TranslationNote, TranslationResult } from '@valyrio/engine/types';
import type { NoteDef, NoteKey } from './note-defs';

export type { NoteKey } from './note-defs';

const NOTE_FILES = import.meta.glob<{ notes: Record<string, NoteDef> }>('./notes/*.notes.ts', { eager: true });

/** Every note the app knows, from the feature notes files (an id declared twice is an error). */
export const NOTE_DEFS: Readonly<Record<NoteKey, NoteDef>> = (() => {
  const all: Record<string, NoteDef> = {};
  for (const [file, m] of Object.entries(NOTE_FILES))
    for (const [id, def] of Object.entries(m.notes)) {
      if (Object.prototype.hasOwnProperty.call(all, id)) throw new Error(`translator note ${id} is declared twice (${file})`);
      all[id] = def;
    }
  return all as Record<NoteKey, NoteDef>;
})();

/**
 * The engine's note ids the app renders, with the names of the arguments in the order the i18n
 * function takes them (engine/src, `notes.add(text, id, args)`).
 */
export const NOTE_ARGS: Readonly<Record<NoteKey, readonly string[]>> = Object.fromEntries(Object.entries(NOTE_DEFS).map(([id, d]) => [id, d.args])) as Record<
  NoteKey,
  readonly string[]
>;

/** The notes' sentences in one UI language (i18n: t.translator.note). */
export function noteStrings(lang: 'fi' | 'en'): Record<NoteKey, (a: string[]) => string> {
  return Object.fromEntries(Object.entries(NOTE_DEFS).map(([id, d]) => [id, d[lang]])) as Record<NoteKey, (a: string[]) => string>;
}

const isNoteKey = (id: string): id is NoteKey => Object.prototype.hasOwnProperty.call(NOTE_ARGS, id);

/** A note as the screen shows it: by id when the app knows it, else by pattern, else null (English). */
export function localNote(n: TranslationNote | string): LocalNote | null {
  if (typeof n === 'string') return matchNote(n);
  if (isNoteKey(n.id)) return { key: n.id, args: NOTE_ARGS[n.id].map((a) => String(n.args?.[a] ?? '')) };
  return matchNote(n.text);
}

/**
 * Engine notes the screen does not show: the Finnish meanings' AI status (checked: the users' decision
 * of 2026-10-05; draft too: Axel 2026-10-22, no AI or status mark on Finnish anywhere in the app).
 */
export const SILENT_NOTE_IDS: ReadonlySet<string> = new Set(['fiChecked', 'fiDraft']);
const SILENT_TEXT = /^Finnish meanings (were confirmed by an AI check|are unreviewed AI drafts)/i;
const silent = (n: TranslationNote): boolean => SILENT_NOTE_IDS.has(n.id) || SILENT_TEXT.test(n.text.trim());

/** The notes of a result as data: the engine's noteItems, or (older results) the sentences alone. */
export function noteItemsOf(r: Pick<TranslationResult, 'notes' | 'noteItems'>): TranslationNote[] {
  return (r.noteItems ?? r.notes.map((text) => ({ id: 'other', text }))).filter((n) => !silent(n));
}

export interface LocalNote {
  key: NoteKey;
  /** Quoted words (HV forms, source words, case names) in the order the i18n function takes them. */
  args: string[];
}

const list = (s: string) =>
  s
    .split(/\s*,\s*/)
    .map((x) => x.trim())
    .filter(Boolean)
    .join(', ');

const PATTERNS: { re: RegExp; key: NoteKey; args?: (m: RegExpExecArray) => string[] }[] = [
  { re: /^No gloss for (.+?); kept the High Valyrian word\.?$/i, key: 'noGloss', args: (m) => [m[1]!] },
  { re: /^Shared subject of coordinated clauses not repeated/i, key: 'sharedSubject' },
  { re: /^Subject pronoun added/i, key: 'pronounAdded' },
  { re: /^Copula "?(be|olla)"? supplied/i, key: 'copula', args: (m) => [m[1]!] },
  { re: /^Finnish meanings are unreviewed AI drafts/i, key: 'fiDraft' },
  { re: /^Some words have no Finnish gloss yet/i, key: 'fiFallback' },
  { re: /^Unsure Finnish forms are shown transparently/i, key: 'fiUnsureForms' },
  { re: /^Finnish numerals take the noun in the singular/i, key: 'fiNumeral' },
  { re: /^Unsure Finnish verb forms are shown transparently/i, key: 'fiUnsureVerbs' },
  { re: /^"(.+?)" was analysed by ending rules only/i, key: 'endingRules', args: (m) => [m[1]!] },
  { re: /^Conjunctive lengthening read as "and" \((.+?)\)/i, key: 'lengthening', args: (m) => [m[1]!] },
  { re: /^Unknown words? kept as is: (.+?)\.?$/i, key: 'unknownKept', args: (m) => [list(m[1]!)] },
  { re: /^Relative clause placed before "lua"/i, key: 'relative' },
  { re: /^Closest available verb form used for (.+?)\.?$/i, key: 'closestVerb', args: (m) => [m[1]!] },
  { re: /^Closest available form used for (.+?)\.?$/i, key: 'closestForm', args: (m) => [m[1]!] },
  { re: /^"and" between nouns: conjunctive lengthening/i, key: 'andLengthening' },
  { re: /^Subject pronoun dropped/i, key: 'pronounDropped' },
  { re: /^"be \(somewhere\)".*→\s*ilagon/i, key: 'ilagon' },
  { re: /^Negative command: infinitive \+ daor/i, key: 'negImperative' },
  { re: /^Negation: the verb is in the subjunctive before "daor"/i, key: 'negSubjunctive' },
  { re: /^Obligation: infinitive, then the one who must in the genitive/i, key: 'obligation' },
  { re: /^Obligation: the one who must is in the dative/i, key: 'obligationDative' },
  { re: /^"(.+?)" kept as a name/i, key: 'keptName', args: (m) => [m[1]!] },
  { re: /^No Finnish glosses in the dictionary yet/i, key: 'noFiGlosses' },
  { re: /^Finnish (translative|abessive) has no direct HV case/i, key: 'approxCase', args: (m) => [m[1]!.toLowerCase()] },
  { re: /^No High Valyrian word found for: (.+?) \(shown in «»\)\.?$/i, key: 'noHvWord', args: (m) => [list(m[1]!)] },
  { re: /^No High Valyrian word found for: (.+?)\.?$/i, key: 'noHvWord', args: (m) => [list(m[1]!)] },
  { re: /^No verb recognised/i, key: 'noVerb' },
  { re: /^Word order changed to High Valyrian SOV/i, key: 'orderToHv' },
  { re: /^Word order changed: HV is verb-final/i, key: 'orderFromHv' },
  { re: /^Past habitual: also "used to/i, key: 'pastHabitual' },
  { re: /^Numeral built by the High Valyrian number rules/i, key: 'hvNumeral' },
  { re: /^Passive agent \("by X"\)/i, key: 'passiveAgent' },
  { re: /^"For" before a clause = "because"/i, key: 'forBecause' },
  { re: /^"of" after a verb: its noun is read as the object/i, key: 'ofObject' },
  { re: /^A clause as the object of a verb of saying or knowing/i, key: 'objectClause' },
  { re: /^"never" = dōrī with a positive verb/i, key: 'never' },
  { re: /^"let X do": X in the vocative before the infinitive/i, key: 'letInfinitive' },
  { re: /^Age: "have seen N years"/i, key: 'age' },
  { re: /^be \+ participle: HV passive voice/i, key: 'passive' },
];

/** The note's key and arguments, or null when the sentence is not one the app knows. */
export function matchNote(note: string): LocalNote | null {
  const s = note.trim();
  for (const p of PATTERNS) {
    const m = p.re.exec(s);
    if (m) return { key: p.key, args: p.args ? p.args(m) : [] };
  }
  return null;
}

/** English "you" as the result read it (the youDefault / youCue / youChosen* note), or undefined. */
export function youOf(r: Pick<TranslationResult, 'notes' | 'noteItems'>): 'sg' | 'pl' | undefined {
  for (const n of noteItemsOf(r)) {
    if (n.id === 'youDefault' || n.id === 'youChosenSg') return 'sg';
    if (n.id === 'youCue' || n.id === 'youChosenPl') return 'pl';
  }
  return undefined;
}

/** The keys of the notes the app knows (by id, else by pattern). */
export function noteKeys(notes: readonly (TranslationNote | string)[]): Set<NoteKey> {
  const out = new Set<NoteKey>();
  for (const n of notes) {
    const m = localNote(n);
    if (m) out.add(m.key);
  }
  return out;
}
